/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Card, Space, Tag } from 'antd';
import { css } from '@emotion/react';
import type { Crop, CropTaxonomyTree } from '../../models/Crop';
import { useCropTaxonomy, findTaxonomyPath } from './useCropTaxonomy';

const styles = {
  attributesCard: css({ marginTop: 16 }),
};

interface CropFormModalProps {
  open: boolean;
  editingCrop: Crop | null;
  taxonomy: CropTaxonomyTree | undefined;
  taxonomyLoading: boolean;
  onSubmit: (values: any) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

/**
 * Модальное окно для создания/редактирования культуры
 */
export const CropFormModal: React.FC<CropFormModalProps> = ({
  open,
  editingCrop,
  taxonomy,
  taxonomyLoading,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [form] = Form.useForm();
  const {
    selectedFamilyId,
    selectedGenusId,
    selectedSpeciesId,
    familyOptions,
    genusOptions,
    speciesOptions,
    cultivarOptions,
    setSelectedFamilyId,
    setSelectedGenusId,
    setSelectedSpeciesId,
    reset,
    initializeFromPath,
  } = useCropTaxonomy(taxonomy);

  // Инициализация формы при открытии
  useEffect(() => {
    if (!open) return;

    if (editingCrop) {
      form.setFieldsValue({
        name: editingCrop.name,
        latinName: editingCrop.latinName,
        speciesId: editingCrop.speciesId ? String(editingCrop.speciesId) : undefined,
        cultivarId: editingCrop.cultivarId ? String(editingCrop.cultivarId) : undefined,
      });

      const path = findTaxonomyPath(taxonomy, editingCrop.speciesId, editingCrop.cultivarId);
      if (path) {
        initializeFromPath(path);
      } else {
        reset();
      }
    } else {
      form.resetFields();
      reset();
    }
  }, [open, editingCrop, form, taxonomy, reset, initializeFromPath]);

  const handleFamilySelect = (value: string | null | undefined) => {
    setSelectedFamilyId(value ?? null);
    setSelectedGenusId(null);
    setSelectedSpeciesId(null);
    form.setFieldsValue({ speciesId: undefined, cultivarId: undefined });
  };

  const handleGenusSelect = (value: string | null | undefined) => {
    setSelectedGenusId(value ?? null);
    setSelectedSpeciesId(null);
    form.setFieldsValue({ speciesId: undefined, cultivarId: undefined });
  };

  const handleSpeciesSelect = (value: string | null | undefined) => {
    const normalized = value ?? null;
    setSelectedSpeciesId(normalized);
    form.setFieldsValue({ speciesId: normalized ?? undefined, cultivarId: undefined });
    
    if (normalized && taxonomy) {
      // Ищем species напрямую в taxonomy, т.к. selectedSpecies может быть ещё undefined
      let foundSpecies = null;
      for (const family of taxonomy.families ?? []) {
        for (const genus of family.genera ?? []) {
          foundSpecies = genus.species?.find((s) => String(s.id) === String(normalized));
          if (foundSpecies) break;
        }
        if (foundSpecies) break;
      }
      
      if (foundSpecies) {
        form.setFieldsValue({
          name: foundSpecies.name ?? '',
          latinName: foundSpecies.latinName ?? '',
        });
      }
    }
  };

  const handleCultivarSelect = (value: string | null | undefined) => {
    form.setFieldsValue({ cultivarId: value ?? undefined });
    
    if (value === null || value === undefined) {
      // Если сорт не выбран, показываем данные вида
      if (selectedSpeciesId && taxonomy) {
        // Ищем species напрямую в taxonomy
        let foundSpecies = null;
        for (const family of taxonomy.families ?? []) {
          for (const genus of family.genera ?? []) {
            foundSpecies = genus.species?.find((s) => String(s.id) === String(selectedSpeciesId));
            if (foundSpecies) break;
          }
          if (foundSpecies) break;
        }
        
        if (foundSpecies) {
          form.setFieldsValue({
            name: foundSpecies.name ?? '',
            latinName: foundSpecies.latinName ?? '',
          });
        }
      }
      return;
    }

    // Если выбран сорт из списка, заполняем его данные
    if (selectedSpeciesId && taxonomy) {
      let foundSpecies = null;
      for (const family of taxonomy.families ?? []) {
        for (const genus of family.genera ?? []) {
          foundSpecies = genus.species?.find((s) => String(s.id) === String(selectedSpeciesId));
          if (foundSpecies) break;
        }
        if (foundSpecies) break;
      }
      
      const cultivar = foundSpecies?.cultivars?.find((item) => String(item.id) === String(value));
      if (cultivar) {
        form.setFieldsValue({
          name: cultivar.name ?? '',
          latinName: cultivar.latinName ?? '',
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
    } catch (error) {
      // Валидационные ошибки обрабатываются формой
    }
  };

  return (
    <Modal
      title={editingCrop ? 'Изменить культуру' : 'Добавить культуру'}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={editingCrop ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={isLoading}
      width={720}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="Семейство">
          <Select
            placeholder="Выберите семейство"
            loading={taxonomyLoading}
            options={familyOptions}
            value={selectedFamilyId ?? undefined}
            onChange={handleFamilySelect}
            allowClear
          />
        </Form.Item>

        <Form.Item label="Род">
          <Select
            key={`genus-${selectedFamilyId}`}
            placeholder="Выберите род"
            loading={taxonomyLoading}
            options={genusOptions}
            value={selectedGenusId ?? undefined}
            onChange={handleGenusSelect}
            disabled={!selectedFamilyId}
            allowClear
          />
        </Form.Item>

        <Form.Item
          label="Вид"
          name="speciesId"
          rules={[{ required: true, message: 'Выберите вид' }]}
        >
          <Select
            placeholder="Выберите вид"
            loading={taxonomyLoading}
            options={speciesOptions}
            onChange={handleSpeciesSelect}
            disabled={!selectedGenusId}
            allowClear
          />
        </Form.Item>

        <Form.Item label="Сорт" name="cultivarId">
          <Select
            key={`cultivar-${selectedSpeciesId}`}
            placeholder="Выберите сорт из списка или оставьте пустым"
            loading={taxonomyLoading}
            options={cultivarOptions}
            onChange={handleCultivarSelect}
            disabled={!selectedSpeciesId || cultivarOptions.length === 0}
            allowClear
          />
        </Form.Item>

        {/*
          Название и латынь культуры больше НЕ вводятся руками: сервер собирает
          их из таксономии (backend/src/domain/catalog/cropIdentity.js) и
          присланные значения игнорирует. Причины две — пользовательский текст
          в crops.name виден всем, кому открыт участок, а латынь из запроса
          ломала дедупликацию культур по (save_id, latin_name). Поля оставлены
          показом: редактируемый ввод, который сервер выбрасывает, врёт.
        */}
        <Form.Item label="Название" name="name">
          <Input disabled />
        </Form.Item>

        <Form.Item label="Латинское название" name="latinName">
          <Input disabled />
        </Form.Item>

        {editingCrop?.attributes && editingCrop.attributes.length > 0 && (
          <Card size="small" type="inner" title="Атрибуты культуры" css={styles.attributesCard}>
            <Space size={[4, 4]} wrap>
              {editingCrop.attributes.map((attribute) => (
                <Tag key={`${editingCrop.id}-${attribute.attributeId}`}>
                  {attribute.name || `#${attribute.attributeId}`}:{' '}
                  {(() => {
                    const baseValue =
                      attribute.choiceTitle ??
                      attribute.choiceValue ??
                      attribute.customValue ??
                      attribute.value ??
                      '—';
                    return attribute.choiceCode && attribute.choiceCode !== baseValue
                      ? `${baseValue} (${attribute.choiceCode})`
                      : baseValue;
                  })()}
                </Tag>
              ))}
            </Space>
          </Card>
        )}
      </Form>
    </Modal>
  );
};
